/**
 * Adapter para manter compatibilidade com a API do makeStyles do MUI v4
 * usando tss-react como backend.
 * 
 * Este adapter permite que o código existente continue funcionando sem alterações.
 */

import React from 'react';
import { makeStyles as tssReactMakeStyles } from 'tss-react/mui';

/**
 * Resolve valores-funcao no formato do JSS (v4): `{ color: props => ... }`.
 * O emotion nao suporta funcao como valor de propriedade e descarta a regra,
 * entao ela e chamada aqui com os params recebidos pelo hook.
 * @param {*} styles - Objeto de estilos (pode ter seletores aninhados)
 * @param {Object} params - Params passados para o useStyles
 * @returns {*} Estilos com os valores-funcao ja resolvidos
 */
const resolveFunctionValues = (styles, params) => {
  if (Array.isArray(styles)) {
    return styles.map((value) => resolveFunctionValues(value, params));
  }

  if (styles === null || typeof styles !== 'object') {
    return styles;
  }

  const resolved = {};

  Object.keys(styles).forEach((key) => {
    const value = styles[key];

    resolved[key] =
      typeof value === 'function' ? value(params) : resolveFunctionValues(value, params);
  });

  return resolved;
};

/**
 * Descarta chaves de primeiro nivel cujo valor nao e um objeto de estilos.
 * O JSS do v4 ignorava esse caso (ex.: um `marginBottom: 25` solto entre as
 * classes); o tss-react tenta anotar cada valor e quebra a tela inteira.
 * @param {Object} styles - Mapa de classe -> estilos
 * @returns {Object} Apenas as entradas que sao objetos
 */
const onlyStyleObjects = (styles) => {
  if (styles === null || typeof styles !== 'object') {
    return {};
  }

  const valid = {};

  Object.keys(styles).forEach((key) => {
    const value = styles[key];

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      valid[key] = value;
    }
  });

  return valid;
};

/**
 * makeStyles compatível com MUI v4
 * @param {Function|Object} stylesOrCreator - Objeto de estilos ou função que recebe o theme
 * @returns {Function} Hook useStyles
 */
export const makeStyles = (stylesOrCreator) => {
  // Criar o hook usando tss-react
  const useStylesInternal = tssReactMakeStyles()((theme, params) => {
    const styles = typeof stylesOrCreator === 'function' ? stylesOrCreator(theme) : stylesOrCreator;

    return onlyStyleObjects(resolveFunctionValues(styles, params || {}));
  });

  // Retornar um wrapper que mantém compatibilidade com a API antiga
  return (props) => {
    const result = useStylesInternal(props);
    return result.classes;
  };
};

// Re-exportar useTheme do MUI
export { useTheme } from '@mui/material/styles';

// Re-exportar styled do MUI
export { styled } from '@mui/material/styles';

// Re-exportar ThemeProvider
export { ThemeProvider } from '@mui/material/styles';

/**
 * withStyles compatível com MUI v4
 * @param {Object|Function} stylesOrCreator - Objeto de estilos ou função que recebe o theme
 * @returns {Function} HOC para adicionar classes ao componente
 */
export const withStyles = (stylesOrCreator) => {
  return (Component) => {
    const useStylesInternal = tssReactMakeStyles()((theme) => {
      if (typeof stylesOrCreator === 'function') {
        return stylesOrCreator(theme);
      }
      return stylesOrCreator;
    });
    
    return (props) => {
      const { classes } = useStylesInternal();
      return <Component {...props} classes={classes} />;
    };
  };
};

export default makeStyles;
